import type { ReactNode } from 'react';
import type { RemixiconComponentType } from '@remixicon/react';

export interface CardTitleProps {
  /** The card's name. A node when the title is itself a control (a toggle). */
  title: ReactNode;
  /** The glyph of the tile, taken from `waadooIcons` (components/ui/icons/waadooIcons.ts). */
  icon?: RemixiconComponentType;
  /** One line under the title, in the secondary ink. */
  description?: ReactNode;
  /** After the title, on its line: a pill, a count, a status chip. */
  aside?: ReactNode;
  /** At the right of the header, vertically centred: buttons, menus. */
  actions?: ReactNode;
  /**
   * The heading level of the title. Without it the title is a plain span,
   * as the card titles that never were headings stay.
   */
  level?: 2 | 3 | 4;
  /** The title's id, when something points at it (aria-labelledby, focus). */
  titleId?: string;
  /** Focusable title (`tabIndex={-1}`), to move the focus to on a change. */
  focusableTitle?: boolean;
  /**
   * The hairline under the header, across the card. Off when nothing is
   * shown under the header (a folded card).
   */
  divider?: boolean;
  /**
   * Spans only, for a header that sits inside a `<button>` (phrasing content
   * alone). No heading level then.
   */
  phrasing?: boolean;
  className?: string;
}

/**
 * The header of a card (mockup « T1 », 2026-10-19): the card's icon on an
 * accent-tinted tile, the title and its description, the actions at the
 * right, a hairline under it all across the card. Every card of the app's
 * own views draws its title with it — BUI `CardHeader`s and `SectionCard`s
 * alike. Laid out by `.waadoo-card-title` in styles.css; the hairline's
 * reach is the card's padding, `--waadoo-card-title-inset`.
 *
 * The tile is decorative: the title says the same. The `{' '}` between the
 * parts keep the words apart for an accessible name computed from the DOM's
 * text alone (jsdom, hence the tests); browsers already separate these flex
 * items, blockified, and draw no whitespace-only text between them.
 */
export function CardTitle(props: CardTitleProps) {
  const {
    title,
    icon: Icon,
    description,
    aside,
    actions,
    level,
    titleId,
    focusableTitle,
    divider = true,
    phrasing = false,
    className,
  } = props;
  const Box = phrasing ? 'span' : 'div';
  const Heading = level && !phrasing ? (`h${level}` as const) : 'span';
  const Description = phrasing ? 'span' : 'p';

  return (
    <Box
      className={['waadoo-card-title', className].filter(Boolean).join(' ')}
      data-divider={divider ? 'true' : undefined}
    >
      <Box className="waadoo-card-title__main">
        {Icon && (
          <span className="waadoo-card-title__tile" aria-hidden="true">
            <Icon
              className="waadoo-card-title__icon"
              aria-hidden
              focusable="false"
            />
          </span>
        )}
        <Box className="waadoo-card-title__text">
          <Box className="waadoo-card-title__line">
            <Heading
              className="waadoo-card-title__title"
              id={titleId}
              tabIndex={focusableTitle ? -1 : undefined}
            >
              {title}
            </Heading>
            {aside && <> {aside}</>}
          </Box>
          {description && (
            <>
              {' '}
              <Description className="waadoo-card-title__description">
                {description}
              </Description>
            </>
          )}
        </Box>
      </Box>
      {actions && <Box className="waadoo-card-title__actions">{actions}</Box>}
    </Box>
  );
}
